import React, { useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { getPollsAverage } from "../../redux/actions/pollingAction";
import { useSearchParams } from "react-router-dom";
import moment from "moment";

const PollingChart = ({ selectedYear }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  useEffect(() => {
    const payload = type
      ? { country: localStorage.getItem("country"), year: selectedYear }
      : {
          country: localStorage.getItem("country"),
          state: localStorage.getItem("state"),
          year: selectedYear,
        };

    dispatch(getPollsAverage(payload));
  }, [dispatch, type, selectedYear]);

  const { pollsAverageResponse, electoralVotesResponse } = useSelector((state) => state?.pollWeb);
  console.log("🚀 ~ PollingChart ~ pollsAverageResponse:", pollsAverageResponse);

  // Process candidates data for colors
  const processCandidatesData = () => {
    if (!electoralVotesResponse || !Array.isArray(electoralVotesResponse)) return {};

    const demCandidate = electoralVotesResponse.find(candidate => 
      candidate.party?.abbreviation === "dem"
    );
    const repCandidate = electoralVotesResponse.find(candidate => 
      candidate.party?.abbreviation === "rep"
    );

    return {
      demColor: demCandidate?.party?.color_code || "#0042C9",
      repColor: repCandidate?.party?.color_code || "#fc0303",
      demName: demCandidate?.name || "Democrat",
      repName: repCandidate?.name || "Republican"
    };
  };

  const candidateData = processCandidatesData();

  // Process polling average data
  const processPollingData = () => {
    if (!pollsAverageResponse || !Array.isArray(pollsAverageResponse)) {
      return {
        sortedData: [],
        categories: [],
        demData: [],
        repData: []
      };
    }

    const sortedData = [...pollsAverageResponse].sort(
      (a, b) => new Date(a.pollDate || a.date || a.createdAt) - new Date(b.pollDate || b.date || b.createdAt)
    );

    const categories = sortedData.map((entry) =>
      moment(entry.pollDate || entry.date || entry.createdAt).format("DD/MM/YYYY")
    );

    // Handle different possible data structures
    const demData = sortedData.map((entry) => {
      return entry.dem_Average ?? entry.democrat ?? entry.dem_value ?? entry.result ?? null;
    });

    const repData = sortedData.map((entry) => {
      return entry.rep_Average ?? entry.republican ?? entry.rep_value ?? entry.result ?? null;
    });

    return {
      sortedData,
      categories,
      demData,
      repData
    };
  };

  const { sortedData, categories, demData, repData } = processPollingData();

  console.log("🚀 ~ Processed demData:", demData);
  console.log("🚀 ~ Processed repData:", repData);
  console.log("🚀 ~ Categories:", categories);

  const options = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: [candidateData.demColor, candidateData.repColor],
    chart: {
      fontFamily: "Satoshi, sans-serif",
      height: 335,
      type: "area",
      dropShadow: {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1,
      },
      toolbar: {
        show: false,
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
        },
      },
      {
        breakpoint: 1366,
        options: {
          chart: {
            height: 350,
          },
        },
      },
    ],
    stroke: {
      width: [2, 2],
      curve: "smooth",
    },
    grid: {
      padding: {
        left: 20,
        right: 20,
      },
      borderColor: '#f1f1f1',
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 4,
      colors: "#fff",
      strokeColors: [candidateData.demColor, candidateData.repColor],
      strokeWidth: 3,
      strokeOpacity: 0.9,
      fillOpacity: 1,
      hover: {
        size: 6,
      }
    },
    xaxis: {
      type: "category",
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          colors: "#64748B",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: "Popular Votes (%)",
        style: {
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
          color: "#64748B",
        },
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
        formatter: function (value) {
          return value + "%";
        }
      },
    },
    tooltip: {
      x: {
        format: 'dd/MM/yyyy'
      },
      y: {
        formatter: function (value) {
          return value + "%";
        }
      }
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.3,
        stops: [0, 90, 100]
      }
    }
  };

  const series = [
    {
      name: candidateData.demName,
      data: demData,
    },
    {
      name: candidateData.repName,
      data: repData,
    },
  ];

  // Check if we have valid data to display
  const hasValidData = demData.some(val => val !== null && val !== undefined) && 
                      repData.some(val => val !== null && val !== undefined) &&
                      categories.length > 0;

  return (
    <div className="col-span-12 my-6 rounded-lg border border-stroke bg-white px-5 pb-5 pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark xl:col-span-8">
      {demData.length && repData.length ? (
        <ReactApexChart
          options={options}
          series={series}
          type="area"
          height={350}
          width="100%"
        />
      ) : (
        <div className="text-center text-gray-500 py-6 text-sm">
          No data available
        </div>
      )}
    </div>
  );
};

export default PollingChart;
